import { Controller } from '@hotwired/stimulus';

// Controller: view-transition-name
// Purpose: Apply a `view-transition-name` to the element AFTER the initial
// View Transition has already run, so it participates only in subsequent
// transitions. This avoids the element being captured in the current transition.
//
// Usage (HTML):
// <div
//   data-controller="view-transition-name"
//   data-view-transition-name-name-value="job-topic-settings"
// ></div>
//
// Notes:
// - When supported, we wait for the `pagereveal` event which fires after the
//   reveal phase of a cross-document View Transition.
// - If unsupported, we fallback to a double requestAnimationFrame so we apply
//   after the current frame and avoid inclusion in the current snapshot.
export default class extends Controller {
  static values = {
    // The view transition name to apply
    name: String,
  };

  connect() {
    // Bind once to allow proper cleanup
    this.onPageReveal = () => this.applyIfPossible();
    this.applied = false;

    // Prefer the `pagereveal` event when available (cross-document transitions)
    if ('onpagereveal' in document) {
      document.addEventListener('pagereveal', this.onPageReveal, {
        once: true,
      });
    } else {
      // Fallback: apply in the next frame (double RAF to be extra safe)
      this.raf1 = requestAnimationFrame(() => {
        this.raf2 = requestAnimationFrame(() => {
          this.applyIfPossible();
        });
      });
    }
  }

  disconnect() {
    if (this.raf1) cancelAnimationFrame(this.raf1);
    if (this.raf2) cancelAnimationFrame(this.raf2);

    // Remove listener if we registered it
    if ('onpagereveal' in document && this.onPageReveal) {
      document.removeEventListener('pagereveal', this.onPageReveal);
    }
  }

  // If the value changes later, update the style only if already applied
  nameValueChanged() {
    if (this.applied && this.hasNameValue) {
      this.element.style.setProperty('view-transition-name', this.nameValue);
    }
  }

  applyIfPossible() {
    if (this.applied) return;

    const name = this.hasNameValue
      ? this.nameValue
      : this.element.dataset.viewTransitionName || this.element.id || '';

    if (!name) return;

    // Apply the CSS property
    this.element.style.setProperty('view-transition-name', name);
    this.applied = true;
  }
}
